import { Injectable } from '@angular/core';

const COOKIE_NAME = 'affiliate_link_hash';
const COOKIE_DAYS = 30;

/**
 * AffiliateTrackingService — Task 9
 *
 * Responsibilities:
 * 1. On app startup: detect ?ref_hash=XYZ in URL and write a 30-day cookie (Last Click Wins).
 * 2. At checkout: expose getTrackingHash() so cart service can attach the hash to the order payload.
 *
 * Cookie strategy:
 * - Always overwrite with the latest ref_hash (Last Click Wins).
 * - Cookie is cleared when the user places an order (optional, called from checkout).
 * - Secure; SameSite=Lax — compatible with most browsers and HTTPS setups.
 */
@Injectable({
  providedIn: 'root',
})
export class AffiliateTrackingService {

  /**
   * Should be called once on AppComponent.ngOnInit().
   * Reads ?ref_hash from the current URL and stores it as a cookie if present.
   */
  captureFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const hash = params.get('ref_hash');
    if (hash && /^[A-Za-z0-9]{4,12}$/.test(hash)) {
      let hashes = this.getTrackingHashes();
      // Remove it if it already exists so we can move it to the end (most recent click)
      hashes = hashes.filter(h => h !== hash);
      hashes.push(hash);
      // Keep only the most recent 10 hashes to prevent cookie bloat
      if (hashes.length > 10) {
        hashes.shift();
      }
      this.setCookie(JSON.stringify(hashes));
    }
  }

  /**
   * Returns the stored affiliate hashes.
   * Used by the checkout flow to attach the affiliate hashes to the order payload.
   */
  getTrackingHashes(): string[] {
    const value = this.getCookie();
    if (!value) return [];
    try {
      if (value.startsWith('[')) {
        return JSON.parse(value);
      }
      return [value]; // Legacy fallback
    } catch {
      return [];
    }
  }

  /**
   * Call after a successful order creation to prevent the same hash
   * being reused on the next order from the same browser session.
   * The 30-day cookie naturally expires even without this call.
   */
  clearTrackingHash(): void {
    this.deleteCookie();
  }

  // ──────────────────────────── Cookie Helpers ────────────────────────────

  private setCookie(value: string): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_DAYS);
    document.cookie = [
      `${COOKIE_NAME}=${encodeURIComponent(value)}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
      // Uncomment for HTTPS-only deployments:
      // 'Secure',
    ].join('; ');
  }

  private getCookie(): string | null {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${COOKIE_NAME}=`));

    return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
  }

  private deleteCookie(): void {
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }
}
